import { Controller } from "@hotwired/stimulus";

/**
 * Tooltip controller — viewport-aware, touch-friendly tooltip positioning.
 *
 * Uses position: fixed so the bubble escapes overflow: hidden containers.
 * Computes top/left from the trigger's getBoundingClientRect() on every show.
 * Auto-flips placement when the bubble would clip a viewport edge.
 *
 * Stimulus wiring (generated by TooltipComponent):
 *   data-controller="application--toolkit--tooltip-component--tooltip-component"
 *   data-application--toolkit--tooltip-component--tooltip-component-placement-value="top"
 *   data-application--toolkit--tooltip-component--tooltip-component-delay-value="200"
 */
export default class extends Controller {
  static targets = ["trigger", "bubble", "arrow"];
  static values = {
    placement: { type: String, default: "top" },
    delay: { type: Number, default: 200 },
  };

  declare triggerTarget: HTMLElement;
  declare bubbleTarget: HTMLElement;
  declare arrowTarget: HTMLElement;
  declare placementValue: string;
  declare delayValue: number;

  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;
  private isVisible = false;
  private touchTriggered = false;

  private static readonly GAP = 8; // px between trigger and bubble

  // ── Lifecycle ────────────────────────────────────

  connect(): void {
    this.bindEvents();
  }

  disconnect(): void {
    this.cleanup();
    this.unbindEvents();
  }

  // ── Event binding ────────────────────────────────

  private bindEvents(): void {
    const trigger = this.triggerTarget;

    trigger.addEventListener("mouseenter", this.handleMouseEnter);
    trigger.addEventListener("mouseleave", this.handleMouseLeave);
    trigger.addEventListener("focusin", this.handleFocusIn);
    trigger.addEventListener("focusout", this.handleFocusOut);
    trigger.addEventListener("touchstart", this.handleTouchStart, {
      passive: true,
    });

    document.addEventListener("touchstart", this.handleOutsideTouch, {
      passive: true,
    });
    document.addEventListener("scroll", this.handleScroll, {
      passive: true,
      capture: true,
    });
    document.addEventListener("keydown", this.handleKeydown);
  }

  private unbindEvents(): void {
    const trigger = this.triggerTarget;

    trigger.removeEventListener("mouseenter", this.handleMouseEnter);
    trigger.removeEventListener("mouseleave", this.handleMouseLeave);
    trigger.removeEventListener("focusin", this.handleFocusIn);
    trigger.removeEventListener("focusout", this.handleFocusOut);
    trigger.removeEventListener("touchstart", this.handleTouchStart);

    document.removeEventListener("touchstart", this.handleOutsideTouch);
    document.removeEventListener("scroll", this.handleScroll, {
      capture: true,
    } as EventListenerOptions);
    document.removeEventListener("keydown", this.handleKeydown);
  }

  // ── Event handlers ───────────────────────────────

  private handleMouseEnter = (): void => {
    if (this.touchTriggered) return;
    this.scheduleShow();
  };

  private handleMouseLeave = (): void => {
    if (this.touchTriggered) return;
    this.scheduleHide();
  };

  private handleFocusIn = (): void => {
    this.show();
  };

  private handleFocusOut = (): void => {
    this.hide();
  };

  private handleTouchStart = (event: TouchEvent): void => {
    event.stopPropagation();
    this.touchTriggered = true;

    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }

    setTimeout(() => {
      this.touchTriggered = false;
    }, 300);
  };

  private handleOutsideTouch = (event: TouchEvent): void => {
    if (!this.isVisible) return;
    if (this.element.contains(event.target as Node)) return;
    this.hide();
  };

  private handleScroll = (): void => {
    if (this.isVisible) {
      this.hide();
    }
  };

  private handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && this.isVisible) {
      this.hide();
    }
  };

  // ── Show / Hide ──────────────────────────────────

  private scheduleShow(): void {
    this.cancelTimers();
    this.showTimeout = setTimeout(() => this.show(), this.delayValue);
  }

  private scheduleHide(): void {
    this.cancelTimers();
    this.hideTimeout = setTimeout(() => this.hide(), 100);
  }

  private show(): void {
    this.cancelTimers();
    this.positionBubble();
    this.bubbleTarget.classList.add("tooltip-component__bubble--visible");
    this.bubbleTarget.setAttribute("aria-hidden", "false");
    this.isVisible = true;
  }

  private hide(): void {
    this.cancelTimers();
    this.bubbleTarget.classList.remove("tooltip-component__bubble--visible");
    this.bubbleTarget.setAttribute("aria-hidden", "true");
    this.isVisible = false;
  }

  private cancelTimers(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private cleanup(): void {
    this.cancelTimers();
    this.hide();
  }

  // ── Fixed positioning ────────────────────────────

  /**
   * Positions the bubble using position: fixed coordinates.
   * 1. Measure trigger rect (viewport-relative already)
   * 2. Make bubble visible off-screen to measure its size
   * 3. Resolve placement (flip if needed)
   * 4. Compute top/left and apply
   * 5. Clamp to viewport and shift arrow if needed
   */
  private positionBubble(): void {
    const bubble = this.bubbleTarget;
    const trigger = this.triggerTarget;
    const gap = (this.constructor as typeof Controller & { GAP: number }).GAP ??  8;

    // ① Reset and measure
    this.setPlacementClass(this.placementValue);
    bubble.style.top = "-9999px";
    bubble.style.left = "-9999px";
    bubble.style.visibility = "hidden";
    bubble.style.opacity = "0";
    bubble.style.display = "block";

    const triggerRect = trigger.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();

    // ② Resolve best placement
    const placement = this.resolvePlacement(
      this.placementValue,
      triggerRect,
      bubbleRect,
      gap
    );
    this.setPlacementClass(placement);

    // ③ Compute fixed coordinates
    let top: number;
    let left: number;

    switch (placement) {
      case "top":
        top = triggerRect.top - bubbleRect.height - gap;
        left = triggerRect.left + triggerRect.width / 2 - bubbleRect.width / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + gap;
        left = triggerRect.left + triggerRect.width / 2 - bubbleRect.width / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - bubbleRect.height / 2;
        left = triggerRect.left - bubbleRect.width - gap;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - bubbleRect.height / 2;
        left = triggerRect.right + gap;
        break;
      default:
        top = triggerRect.top - bubbleRect.height - gap;
        left = triggerRect.left + triggerRect.width / 2 - bubbleRect.width / 2;
    }

    // ④ Clamp to viewport edges
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let arrowShift = 0;

    // Horizontal clamp (for top/bottom placements)
    if (placement === "top" || placement === "bottom") {
      if (left < margin) {
        arrowShift = left - margin; // negative = shifted right
        left = margin;
      } else if (left + bubbleRect.width > vw - margin) {
        arrowShift = left + bubbleRect.width - (vw - margin); // positive = shifted left
        left = vw - margin - bubbleRect.width;
      }
    }

    // Vertical clamp (for left/right placements)
    if (placement === "left" || placement === "right") {
      if (top < margin) {
        top = margin;
      } else if (top + bubbleRect.height > vh - margin) {
        top = vh - margin - bubbleRect.height;
      }
    }

    // ⑤ Apply position
    bubble.style.top = `${top}px`;
    bubble.style.left = `${left}px`;
    bubble.style.visibility = "";
    bubble.style.opacity = "";
    bubble.style.display = "";

    // ⑥ Shift arrow to stay aligned with trigger center
    this.positionArrow(placement, arrowShift);
  }

  private resolvePlacement(
    preferred: string,
    triggerRect: DOMRect,
    bubbleRect: DOMRect,
    gap: number
  ): string {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const fits: Record<string, boolean> = {
      top: triggerRect.top - bubbleRect.height - gap >= 0,
      bottom: triggerRect.bottom + bubbleRect.height + gap <= vh,
      left: triggerRect.left - bubbleRect.width - gap >= 0,
      right: triggerRect.right + bubbleRect.width + gap <= vw,
    };

    if (fits[preferred]) return preferred;

    const opposites: Record<string, string> = {
      top: "bottom",
      bottom: "top",
      left: "right",
      right: "left",
    };
    const opposite = opposites[preferred];
    if (fits[opposite]) return opposite;

    for (const dir of ["top", "bottom", "right", "left"]) {
      if (fits[dir]) return dir;
    }

    return preferred;
  }

  private setPlacementClass(placement: string): void {
    const bubble = this.bubbleTarget;
    bubble.classList.remove(
      "tooltip-component__bubble--top",
      "tooltip-component__bubble--bottom",
      "tooltip-component__bubble--left",
      "tooltip-component__bubble--right"
    );
    bubble.classList.add(`tooltip-component__bubble--${placement}`);
  }

  /**
   * When the bubble was clamped horizontally, shift the arrow
   * so it still points at the trigger center.
   */
  private positionArrow(placement: string, shift: number): void {
    const arrow = this.arrowTarget;
    arrow.style.left = "";
    arrow.style.top = "";
    arrow.style.transform = "";

    if ((placement === "top" || placement === "bottom") && shift !== 0) {
      // shift > 0 means bubble was pushed left, arrow needs to go right
      arrow.style.left = `calc(50% + ${shift}px)`;
      arrow.style.transform = "translateX(-50%)";
    }
  }
}
